import { CheckCircle, Copy, X } from "react-feather";
import styles from "./Log.module.scss";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Axios from "src/services/api";
import toast from "react-hot-toast";
import { PageLoader } from "src/components/page-loader";
import { CodeBlock } from "react-code-block";
import { themes } from "prism-react-renderer";
import dayjs from "dayjs";

const Log = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [log, setLog] = useState<any>(null);
  const [copied, setCopied] = useState(false);
  const [sigCopied, setSigCopied] = useState(false);

  useEffect(() => {
    if (id) {
      setLoading(true);
      Axios.get(`/delivered-webhooks/${id}`)
        .then((res) => {
          setLog(res.data.deliveredWebhook);
          setLoading(false);
        })
        .catch(() => {
          toast.error("Failed to fetch webhook log");
          setLoading(false);
        });
    }
  }, [id]);

  // Reset copied state after 3s
  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 3000);
    }
    if (sigCopied) {
      setTimeout(() => {
        setSigCopied(false);
      }, 3000);
    }
  }, [copied, sigCopied]);

  const handleBack = () => {
    navigate("/developers/logs");
  };

  const copySignature = () => {
    if (log?.sig) {
      navigator.clipboard.writeText(log.sig);
      setSigCopied(true);
      toast.success("Signature copied to clipboard");
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  const formattedWebhookData = log?.webhookData ? JSON.stringify(JSON.parse(log.webhookData), null, 2) : "";

  return (
    <div className={styles.addWebhookContainer}>
      <div className={styles.left}>
        <div className={styles.nav}>
          <div className={styles.closeContainer} onClick={handleBack}>
            <X size={20} />
          </div>
          <div className={styles.divider}></div>
          <div className={styles.title}>Webhook Log</div>
          <div
            className={`${styles.activeItem} ${log?.isSuccessful ? styles.active : ""}`}
          >
            <span>{log?.isSuccessful ? "Success" : "Failed"}</span>
          </div>
        </div>
        <div className={styles.formContainer}>
          <h1 className={styles.pageTitle}>Webhook Delivery Details</h1>
          
          <div className={styles.webhookFormGrid}>
            <div>
              <div className={styles.detailItem}>
                <div className={styles.detailLabel}>Event</div>
                <div className={styles.detailValue}>{log?.event}</div>
              </div>
              <div className={styles.detailItem}>
                <div className={styles.detailLabel}>Signature</div>
                <div className={styles.detailValue}>
                  <div className={styles.signatureContainer}>
                    <div className={styles.signatureText}>
                      {log?.sig ?? "N/A"}
                    </div>
                    {log?.sig && (
                      <div 
                        className={styles.signatureCopy} 
                        onClick={copySignature}
                      >
                        {sigCopied ? <CheckCircle size={16} /> : <Copy size={16} />}
                      </div>
                    )}
                  </div>
                </div>
              </div>
  
              <div className={styles.detailItem}>
                <div className={styles.detailLabel}>Webhook ID</div>
                <div className={styles.detailValue}>
                  <Link 
                    to={`/developers/webhooks/${log?.webhookId}`} 
                    className={styles.webhookLink}
                  >
                    {log?.webhookId}
                  </Link>
                </div>
              </div>
              
              <div className={styles.detailItem}>
                <div className={styles.detailLabel}>Sent Date</div>
                <div className={styles.detailValue}>
                  {dayjs(log?.sentDate).format("MMM D, YYYY h:mm A")}
                </div>
              </div>
              
              <div className={styles.detailItem}>
                <div className={styles.detailLabel}>Status</div>
                <div className={`${styles.detailStatus} ${log?.isSuccessful ? styles.success : styles.failed}`}>
                  {log?.isSuccessful ? "Success" : "Failed"}
                </div>
              </div>
              
              <div className={styles.detailItem}>
                <div className={styles.detailLabel}>Test Mode</div>
                <div className={styles.detailValue}>
                  {log?.testMode ? "Yes" : "No"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.right}>
        <div
          className={styles.copy}
          onClick={() => {
            navigator.clipboard.writeText(formattedWebhookData);
            setCopied(true);
          }}
        >
          {copied ? <CheckCircle size={15} /> : <Copy size={15} />}
          {copied ? "Copied" : "Copy"}
        </div>
        <div className={styles.codeContainer}>
          <div className={styles.dataTitle}>Webhook Data</div>
          {log?.webhookData && (
            <CodeBlock
              code={formattedWebhookData}
              language="json"
              theme={themes.nightOwlLight}
            >
              <CodeBlock.Code className={styles.container}>
                <div className={styles.row}>
                  <CodeBlock.LineNumber className={styles.lineNumber} />
                  <CodeBlock.LineContent className={styles.lineContent}>
                    <CodeBlock.Token />
                  </CodeBlock.LineContent>
                </div>
              </CodeBlock.Code>
            </CodeBlock>
          )}
        </div>
      </div>
    </div>
  );
};

export default Log; 